import React, { useEffect, useState, useContext } from 'react'
import ModalTest from '../../../BaseModal'
import { Button, ButtonContainer, CloseDiv, ContainerOfModal, Header, HeaderTitle, ViewContainer, ViewItem } from '../../../styles/modal-style'
import { useNavigate, useSearchParams } from 'react-router-dom'
import axios from '../../../../config/axios'
import { X, Trash2 } from 'lucide-react'
import { Label } from '../../../styles/select-style'
import editPen from '../../../../assets/certificates/Tables/pencil.svg'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import ConfirmationModal from '../../../ModalConfirmation'
import { secondsToHHmm } from '../../../../utils/time'

interface Machine {
  id: number
  name: string
}

interface Supplier {
  id: number
  companyName: string
  contactName?: string
}

interface Category {
  id: number
  name: string
}

interface Type {
  id: number
  name: string
}

interface Service {
  id: number
  name: string
  code: string
  category: Category | null
  type: Type | null
  pricePerHour: number
  prices?: Array<{
    id: number
    price: string
    unitOfMeasurement: {
      id: number
      name: string
    }
  }>
  deadlineInDays: number
  executionTime?: number | null
  setupTime?: number | null
  machines: Machine[]
  suppliers: Supplier[]
  description?: string
  observation?: string
  sector?: { id: number; name: string; Area?: { id: number; name: string } | null } | null
  Category_Stage?: Category | null
  Type_Category_Stage?: Type | null
  StagePrice?: any[]
  Sector?: any
}

const ViewServiceStageModal = () => {
  const [ searchParams ] = useSearchParams()
  const id = searchParams.get('id')
  const navigate = useNavigate()
  const [ service, setService ] = useState<Service | null>(null)
  const { setFeedback } = useContext(FeedbackContext)
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, serviceId: null as number | null, serviceName: '' })

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        try {
          const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/services/service-stage/${id}`)
          const data = response.data
          setService(data)
        } catch (error) {
          console.log(error)
        }
      }
    }
    fetchData()
  }, [id])

  const formatPrices = (prices?: any[]) => {
    if (!prices || prices.length === 0) return 'N/A'
    
    return prices.map(price => {
      const formattedPrice = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(parseFloat(price.price))
      return `${formattedPrice}/${price.unitOfMeasurement?.name || price.UnitOfMeasurement?.name || 'unidade'}`
    }).join(', ')
  }

  const formatDeadline = (deadline?: number | null) => {
    if (!deadline) return 'N/A'
    return `${deadline} ${deadline === 1 ? 'dia útil' : 'dias úteis'}`
  }

  const getSupplierDisplayName = (supplier: any): string => {
    if (!supplier) return ''
    const baseName = supplier.companyName || supplier.contactName || ''
    if (!supplier.isMain) return baseName
    const tipo = supplier.tipoEstablishment || supplier.tipoEstabelecimento || (supplier.cnpj ? (supplier.cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial') : '')
    return `${baseName}${tipo ? ` - ${tipo}` : ''}`
  }

  const handleDeleteClick = () => {
    if (!id || !service) return
    setDeleteModal({ isOpen: true, serviceId: parseInt(id), serviceName: service.name })
  }

  const handleConfirmDelete = async () => {
    if (!deleteModal.serviceId) return

    try {
      await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/services/service-stage/${deleteModal.serviceId}`)
      setFeedback({ 
        message: 'Serviço excluído com sucesso!', 
        isOpen: true, 
        statusOk: true 
      })
      navigate('/panel/service')
    } catch (error: any) {
      let errorMessage = 'Erro ao excluir serviço'
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }

      setFeedback({ 
        message: errorMessage, 
        isOpen: true, 
        statusOk: false 
      })
    } finally {
      setDeleteModal({ isOpen: false, serviceId: null, serviceName: '' })
    }
  }

  const handleCancelDelete = () => {
    setDeleteModal({ isOpen: false, serviceId: null, serviceName: '' })
  }

  return (
    <>
      <ModalTest>
        <ContainerOfModal width='800px'>
          <Header>
            <HeaderTitle>Serviço: {service?.name}</HeaderTitle>
            <CloseDiv onClick={() => navigate('..')}> <X size={20} /></CloseDiv>
          </Header>        
          <ViewContainer>
            <ViewItem>
              <span>Área</span>
              <Label>{service?.sector?.Area?.name || service?.Sector?.Area?.name || 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Setor</span>
              <Label>{service?.sector?.name || service?.Sector?.name || 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Categoria</span>
              <Label>{service?.category?.name || service?.Category_Stage?.name || 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Tipo</span>
              <Label>{service?.type?.name || service?.Type_Category_Stage?.name || 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Prazo</span>
              <Label>{formatDeadline(service?.deadlineInDays)}</Label>
            </ViewItem>
            <ViewItem>
              <span>Código</span>
              <Label>{service?.code || 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Preços</span>
              <Label>{formatPrices(service?.prices || service?.StagePrice)}</Label>
            </ViewItem>
            <ViewItem>
              <span>Tempo de execução</span>
              <Label>{secondsToHHmm(service?.executionTime as any) || 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Tempo de setup</span>
              <Label>{secondsToHHmm(service?.setupTime as any) || 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Máquinas</span>
              <Label>{service?.machines.map((machine: any) => machine.name).join(', ') || 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Fornecedores</span>
              <Label>{service?.suppliers.map((supplier: any) => getSupplierDisplayName(supplier)).join(', ') || 'N/A'}</Label>
            </ViewItem>
            <ViewItem style={{ gridColumn: '1 / -1' }}>
              <span>Descrição detalhada</span>
              <Label style={{ whiteSpace: 'pre-wrap' }}>{service?.description || 'N/A'}</Label>
            </ViewItem>
            <ViewItem style={{ gridColumn: '1 / -1' }}>
              <span>Observações</span>
              <Label style={{ whiteSpace: 'pre-wrap' }}>{service?.observation || 'N/A'}</Label>
            </ViewItem>
          </ViewContainer>
          <ButtonContainer>
            <Button 
              onClick={handleDeleteClick}
              style={{
                backgroundColor: '#F87171',
                color: '#FFFFFF',
                border: '1px solid #F87171'
              }}
            >
              <Trash2 size={16} /> Excluir
            </Button>
            <Button 
              onClick={() => navigate(`/panel/service/edit/?id=${id}`)} 
              className='cancel'
            >
              <img src={editPen} alt='edit' /> Editar
            </Button>
          </ButtonContainer>
        </ContainerOfModal>
      </ModalTest>

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        title="Confirmar Exclusão"
        message={
          <div>
            <p>Tem certeza que deseja excluir o serviço: &quot;{deleteModal.serviceName}&quot;?</p>
            <p>Esta ação não pode ser desfeita.</p>
          </div>
        }
        actions={[
          { label: 'Cancelar', onClick: handleCancelDelete, variant: 'outline' },
          { label: 'Excluir', onClick: handleConfirmDelete, variant: 'danger' },
        ]}
      />
    </>
  )
}

export default ViewServiceStageModal
